import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from '@/hooks/use-toast';
import { 
  Building2, 
  Globe, 
  Mail, 
  Send, 
  Video, 
  Facebook, 
  Instagram, 
  Linkedin, 
  Save, 
  Loader2, 
  CheckCircle2,
  ExternalLink,
  Share2
} from 'lucide-react';

interface AppSettings {
  id: string;
  brand_name: string;
  practitioner_display_name: string | null;
  public_city: string | null;
  public_site_url: string;
  facebook_url: string | null;
  instagram_url: string | null;
  linkedin_url: string | null;
  contact_email_public: string;
  notification_email_internal: string;
  transactional_from_name: string;
  transactional_reply_to: string;
  visio_provider: 'google_meet' | 'zoom' | 'custom';
  visio_default_url: string | null;
  visio_generate_per_appointment: boolean;
  // Make.com social publishing
  make_webhook_social_url: string | null;
  autopost_facebook: boolean;
  autopost_instagram: boolean;
  autopost_linkedin: boolean;
  autostory_instagram: boolean;
  autostory_facebook: boolean;
  social_publish_enabled: boolean;
  social_publish_mode: 'test' | 'production';
  seo_section_enabled: boolean;
}

const DEFAULT_SETTINGS: Omit<AppSettings, 'id'> = {
  brand_name: 'REGARD DE NATURE',
  practitioner_display_name: null,
  public_city: null,
  public_site_url: 'https://www.regard-de-nature.fr',
  facebook_url: null,
  instagram_url: null,
  linkedin_url: null,
  contact_email_public: 'contact@regard-de-nature.fr',
  notification_email_internal: 'contact@regard-de-nature.fr',
  transactional_from_name: 'REGARD DE NATURE',
  transactional_reply_to: 'contact@regard-de-nature.fr',
  visio_provider: 'google_meet',
  visio_default_url: null,
  visio_generate_per_appointment: true,
  make_webhook_social_url: null,
  autopost_facebook: false,
  autopost_instagram: false,
  autopost_linkedin: false,
  autostory_instagram: false,
  autostory_facebook: false,
  social_publish_enabled: false,
  social_publish_mode: 'test',
  seo_section_enabled: true,
};

export function AppSettingsForm() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSocial, setTestingSocial] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .limit(1)
        .single();

      if (error) throw error;
      setSettings(data as AppSettings);
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les paramètres.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('app_settings')
        .update({
          brand_name: settings.brand_name,
          practitioner_display_name: settings.practitioner_display_name || null,
          public_city: settings.public_city || null,
          public_site_url: settings.public_site_url,
          facebook_url: settings.facebook_url || null,
          instagram_url: settings.instagram_url || null,
          linkedin_url: settings.linkedin_url || null,
          contact_email_public: settings.contact_email_public,
          notification_email_internal: settings.notification_email_internal,
          transactional_from_name: settings.transactional_from_name,
          transactional_reply_to: settings.transactional_reply_to,
          visio_provider: settings.visio_provider,
          visio_default_url: settings.visio_default_url || null,
          visio_generate_per_appointment: settings.visio_generate_per_appointment,
          make_webhook_social_url: settings.make_webhook_social_url || null,
          autopost_facebook: settings.autopost_facebook,
          autopost_instagram: settings.autopost_instagram,
          autopost_linkedin: settings.autopost_linkedin,
          autostory_instagram: settings.autostory_instagram,
          autostory_facebook: settings.autostory_facebook,
          social_publish_enabled: settings.social_publish_enabled,
          social_publish_mode: settings.social_publish_mode,
          seo_section_enabled: settings.seo_section_enabled,
        })
        .eq('id', settings.id);

      if (error) throw error;

      toast({
        title: "Paramètres enregistrés",
        description: "Vos modifications ont été sauvegardées.",
      });
    } catch (error: any) {
      console.error('Erreur sauvegarde:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer les paramètres.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    if (!settings?.notification_email_internal) return;

    setTestingEmail(true);
    try {
      const { data, error } = await supabase.functions.invoke('test-notification-email', {
        body: {
          email: settings.notification_email_internal,
          fromName: settings.transactional_from_name,
          replyTo: settings.transactional_reply_to,
        }
      });

      if (error) throw error;

      toast({
        title: "Email envoyé",
        description: `Un email de test a été envoyé à ${settings.notification_email_internal}.`,
      });
    } catch (error: any) {
      console.error('Erreur envoi test:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer l'email de test.",
        variant: "destructive",
      });
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestSocialPublish = async () => {
    if (!settings?.make_webhook_social_url) {
      toast({
        title: "Configuration manquante",
        description: "Veuillez d'abord configurer l'URL du webhook Make.com.",
        variant: "destructive",
      });
      return;
    }

    setTestingSocial(true);
    try {
      const { data, error } = await supabase.functions.invoke('social-push-to-make', {
        body: {
          type: 'test',
          id: 'test-social-publish-001',
          isTest: true,
        }
      });

      if (error) throw error;

      toast({
        title: "Test réussi ✓",
        description: "Le payload de test a été envoyé à Make.com. Vérifiez l'historique de votre scénario.",
      });
    } catch (error: any) {
      console.error('Erreur test social:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'envoyer le test vers Make.com.",
        variant: "destructive",
      });
    } finally {
      setTestingSocial(false);
    }
  };

  const updateSetting = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: value });
  };

  const validateUrl = (url: string): boolean => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Paramètres non disponibles
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Identité */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Identité
          </CardTitle>
          <CardDescription>
            Informations publiques affichées sur le site
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="brand_name">Nom de marque</Label>
              <Input
                id="brand_name"
                value={settings.brand_name}
                onChange={(e) => updateSetting('brand_name', e.target.value)}
                placeholder="REGARD DE NATURE"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="practitioner_display_name">Nom affiché (praticienne)</Label>
              <Input
                id="practitioner_display_name"
                value={settings.practitioner_display_name || ''}
                onChange={(e) => updateSetting('practitioner_display_name', e.target.value)}
                placeholder="Laura"
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="public_city">Ville</Label>
              <Input
                id="public_city"
                value={settings.public_city || ''}
                onChange={(e) => updateSetting('public_city', e.target.value)}
                placeholder="Nantes"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="public_site_url">URL du site</Label>
              <Input
                id="public_site_url"
                type="url"
                value={settings.public_site_url}
                onChange={(e) => updateSetting('public_site_url', e.target.value)}
                placeholder="https://www.regard-de-nature.fr"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section Réseaux sociaux */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Réseaux sociaux
          </CardTitle>
          <CardDescription>
            Liens affichés dans le footer du site (laissez vide pour masquer)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="facebook_url" className="flex items-center gap-2">
              <Facebook className="w-4 h-4 text-blue-600" />
              Facebook
            </Label>
            <Input
              id="facebook_url"
              type="url"
              value={settings.facebook_url || ''}
              onChange={(e) => updateSetting('facebook_url', e.target.value)}
              placeholder="https://www.facebook.com/votre-page"
              className={settings.facebook_url && !validateUrl(settings.facebook_url) ? 'border-destructive' : ''}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="instagram_url" className="flex items-center gap-2">
              <Instagram className="w-4 h-4 text-pink-600" />
              Instagram
            </Label>
            <Input
              id="instagram_url"
              type="url"
              value={settings.instagram_url || ''}
              onChange={(e) => updateSetting('instagram_url', e.target.value)}
              placeholder="https://www.instagram.com/votre-compte"
              className={settings.instagram_url && !validateUrl(settings.instagram_url) ? 'border-destructive' : ''}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="linkedin_url" className="flex items-center gap-2">
              <Linkedin className="w-4 h-4 text-blue-700" />
              LinkedIn
            </Label>
            <Input
              id="linkedin_url"
              type="url"
              value={settings.linkedin_url || ''}
              onChange={(e) => updateSetting('linkedin_url', e.target.value)}
              placeholder="https://www.linkedin.com/in/votre-profil"
              className={settings.linkedin_url && !validateUrl(settings.linkedin_url) ? 'border-destructive' : ''}
            />
          </div>
        </CardContent>
      </Card>

      {/* Section Emails */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Emails
          </CardTitle>
          <CardDescription>
            Configuration des emails (ne modifie pas les providers existants)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="contact_email_public">Email public (affiché sur le site)</Label>
              <Input
                id="contact_email_public"
                type="email"
                value={settings.contact_email_public}
                onChange={(e) => updateSetting('contact_email_public', e.target.value)}
                placeholder="contact@regard-de-nature.fr"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notification_email_internal">Email notifications (RDV, commandes...)</Label>
              <Input
                id="notification_email_internal"
                type="email"
                value={settings.notification_email_internal}
                onChange={(e) => updateSetting('notification_email_internal', e.target.value)}
                placeholder="contact@regard-de-nature.fr"
              />
            </div>
          </div>

          <Separator />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="transactional_from_name">Nom expéditeur (emails transactionnels)</Label>
              <Input
                id="transactional_from_name"
                value={settings.transactional_from_name}
                onChange={(e) => updateSetting('transactional_from_name', e.target.value)}
                placeholder="REGARD DE NATURE"
              />
              <p className="text-xs text-muted-foreground">
                Affiché comme "De: Nom"
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="transactional_reply_to">Répondre à (reply-to)</Label>
              <Input
                id="transactional_reply_to"
                type="email"
                value={settings.transactional_reply_to}
                onChange={(e) => updateSetting('transactional_reply_to', e.target.value)}
                placeholder="contact@regard-de-nature.fr"
              />
              <p className="text-xs text-muted-foreground">
                Quand le client répond à un email
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-4">
            <Button
              variant="outline"
              onClick={handleTestNotification}
              disabled={testingEmail || !settings.notification_email_internal}
            >
              {testingEmail ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Tester la notification
            </Button>
            <span className="text-sm text-muted-foreground">
              Envoie un email test à {settings.notification_email_internal}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Section Visio */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="w-5 h-5" />
            Visioconférence
          </CardTitle>
          <CardDescription>
            Configuration des liens de consultation en ligne
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="visio_provider">Service de visio</Label>
            <Select
              value={settings.visio_provider}
              onValueChange={(value: 'google_meet' | 'zoom' | 'custom') => 
                updateSetting('visio_provider', value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="google_meet">Google Meet</SelectItem>
                <SelectItem value="zoom">Zoom</SelectItem>
                <SelectItem value="custom">Lien personnalisé</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Générer un lien par rendez-vous</Label>
              <p className="text-sm text-muted-foreground">
                {settings.visio_generate_per_appointment
                  ? "Un nouveau lien unique sera créé pour chaque RDV"
                  : "Un lien fixe sera utilisé pour tous les RDV"}
              </p>
            </div>
            <Switch
              checked={settings.visio_generate_per_appointment}
              onCheckedChange={(checked) => updateSetting('visio_generate_per_appointment', checked)}
            />
          </div>

          {!settings.visio_generate_per_appointment && (
            <div className="space-y-2">
              <Label htmlFor="visio_default_url">Lien de visio par défaut</Label>
              <Input
                id="visio_default_url"
                type="url"
                value={settings.visio_default_url || ''}
                onChange={(e) => updateSetting('visio_default_url', e.target.value)}
                placeholder="https://meet.google.com/xxx-xxxx-xxx"
              />
              <p className="text-xs text-muted-foreground">
                Ce lien sera utilisé pour toutes les consultations en visio
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section Make.com / Publication Sociale */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Publication réseaux sociaux (Make.com)
          </CardTitle>
          <CardDescription>
            Automatisez la publication de vos contenus sur les réseaux sociaux via Make.com
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Activation et Mode */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/30">
              <div className="space-y-0.5">
                <Label htmlFor="social_publish_enabled" className="font-medium">Automatisation active</Label>
                <p className="text-xs text-muted-foreground">
                  {settings.social_publish_enabled 
                    ? "Les publications seront envoyées vers Make.com" 
                    : "Les publications sont désactivées"}
                </p>
              </div>
              <Switch
                id="social_publish_enabled"
                checked={settings.social_publish_enabled}
                onCheckedChange={(checked) => updateSetting('social_publish_enabled', checked)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="social_publish_mode">Mode de publication</Label>
              <Select
                value={settings.social_publish_mode}
                onValueChange={(value: 'test' | 'production') => 
                  updateSetting('social_publish_mode', value)
                }
              >
                <SelectTrigger className={settings.social_publish_mode === 'production' ? 'border-green-500 bg-green-50 dark:bg-green-950' : 'border-amber-500 bg-amber-50 dark:bg-amber-950'}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="test">🧪 Test (aucune publication réelle)</SelectItem>
                  <SelectItem value="production">🚀 Production (publication réelle)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {settings.social_publish_mode === 'test' 
                  ? "En mode test, aucune publication réelle n'est effectuée"
                  : "⚠️ En production, les contenus seront publiés sur vos réseaux"}
              </p>
            </div>
          </div>

          {/* Indicateur visuel du statut */}
          {settings.social_publish_enabled && (
            <div className={`flex items-center gap-3 p-4 rounded-lg border-2 ${
              settings.social_publish_mode === 'production' 
                ? 'border-green-500 bg-green-50 dark:bg-green-950' 
                : 'border-amber-500 bg-amber-50 dark:bg-amber-950'
            }`}>
              <CheckCircle2 className={`w-5 h-5 ${settings.social_publish_mode === 'production' ? 'text-green-600' : 'text-amber-600'}`} />
              <div>
                <p className="font-medium text-sm">
                  {settings.social_publish_mode === 'production' 
                    ? 'Automatisation réseaux active (PRODUCTION)' 
                    : 'Automatisation réseaux active (TEST)'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {settings.social_publish_mode === 'production'
                    ? 'Les contenus seront publiés sur vos réseaux sociaux'
                    : 'Les payloads seront envoyés à Make.com sans publication réelle'}
                </p>
              </div>
            </div>
          )}

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="make_webhook_social_url">URL Webhook Make.com</Label>
            <Input
              id="make_webhook_social_url"
              type="url"
              value={settings.make_webhook_social_url || ''}
              onChange={(e) => updateSetting('make_webhook_social_url', e.target.value)}
              placeholder="https://hook.eu2.make.com/xxx..."
              className={settings.make_webhook_social_url && !validateUrl(settings.make_webhook_social_url) ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              Créez un scénario Make.com avec un webhook et collez l'URL ici
            </p>
          </div>

          {/* Bouton Test */}
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={handleTestSocialPublish}
              disabled={testingSocial || !settings.make_webhook_social_url}
            >
              {testingSocial ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Tester la publication réseaux
            </Button>
            <span className="text-sm text-muted-foreground">
              Envoie un payload de test vers Make.com
            </span>
          </div>

          <Separator />

          {/* Posts */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Activer la publication automatique des POSTS</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <Label htmlFor="autopost_facebook" className="flex items-center gap-2 cursor-pointer">
                  <Facebook className="w-4 h-4 text-blue-600" />
                  Facebook
                </Label>
                <Switch
                  id="autopost_facebook"
                  checked={settings.autopost_facebook}
                  onCheckedChange={(checked) => updateSetting('autopost_facebook', checked)}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-4">
                <Label htmlFor="autopost_instagram" className="flex items-center gap-2 cursor-pointer">
                  <Instagram className="w-4 h-4 text-pink-600" />
                  Instagram
                </Label>
                <Switch
                  id="autopost_instagram"
                  checked={settings.autopost_instagram}
                  onCheckedChange={(checked) => updateSetting('autopost_instagram', checked)}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-4">
                <Label htmlFor="autopost_linkedin" className="flex items-center gap-2 cursor-pointer">
                  <Linkedin className="w-4 h-4 text-blue-700" />
                  LinkedIn
                </Label>
                <Switch
                  id="autopost_linkedin"
                  checked={settings.autopost_linkedin}
                  onCheckedChange={(checked) => updateSetting('autopost_linkedin', checked)}
                />
              </div>
            </div>
          </div>

          {/* Stories */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Activer la publication automatique des STORIES</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <Label htmlFor="autostory_instagram" className="flex items-center gap-2 cursor-pointer">
                  <Instagram className="w-4 h-4 text-pink-600" />
                  Story Instagram
                </Label>
                <Switch
                  id="autostory_instagram"
                  checked={settings.autostory_instagram}
                  onCheckedChange={(checked) => updateSetting('autostory_instagram', checked)}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-4">
                <Label htmlFor="autostory_facebook" className="flex items-center gap-2 cursor-pointer">
                  <Facebook className="w-4 h-4 text-blue-600" />
                  Story Facebook
                </Label>
                <Switch
                  id="autostory_facebook"
                  checked={settings.autostory_facebook}
                  onCheckedChange={(checked) => updateSetting('autostory_facebook', checked)}
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
            <p className="font-medium mb-1">💡 Comment ça fonctionne :</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Activez l'automatisation et choisissez le mode (Test ou Production)</li>
              <li>Activez les canaux souhaités ci-dessus</li>
              <li>Lors de la création d'un contenu, sélectionnez les canaux</li>
              <li>Cliquez sur "Publier" pour envoyer vers Make.com</li>
              <li>En mode Test, vérifiez l'historique Make.com avant de passer en Production</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Section SEO - Domaines d'accompagnement */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Section SEO sur la page d'accueil
          </CardTitle>
          <CardDescription>
            Affiche un bloc "Domaines d'accompagnement" avec des liens vers les pages thématiques
            (Montesquieu, Villefranche, stress, digestion, sommeil, fatigue, iridologie, fleurs de Bach).
            Améliore le référencement local et le maillage interne.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="seo_section_enabled" className="cursor-pointer">
                Afficher la section "Domaines d'accompagnement"
              </Label>
              <p className="text-sm text-muted-foreground">
                Visible publiquement après la section "Une approche née de mon parcours"
              </p>
            </div>
            <Switch
              id="seo_section_enabled"
              checked={settings.seo_section_enabled}
              onCheckedChange={(checked) => updateSetting('seo_section_enabled', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Bouton de sauvegarde */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer les paramètres
        </Button>
      </div>
    </div>
  );
}
